/**
 * Solvane header — mobile drawer + sticky behaviour.
 *
 * Custom element so it initialises per instance and survives theme-editor
 * section re-renders without needing a re-init hook.
 *
 * Drawer accessibility: focus moves into the panel on open and back to the
 * trigger on close, Escape closes, and focus is trapped while open. That is
 * the bar for a modal dialog and it is why this isn't done with a CSS-only
 * checkbox toggle.
 *
 * Sticky: hides on scroll down, reveals on scroll up. rAF-throttled so the
 * scroll handler never runs more than once per frame.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE BUG THIS FILE WAS REWRITTEN TO FIX — 21 Aug 2026
 *
 * "Narrow the window to mobile, click the menu, the page stops scrolling and
 * gets stuck." Three faults, all of which had to go:
 *
 * 1. THE LOCK LEAKED ACROSS THE BREAKPOINT. `.sv-header__burger` is
 *    `display:none` at 990px, but nothing closed the drawer when the viewport
 *    crossed that line. Open it at mobile width, widen the window, and the
 *    burger vanishes while `body { overflow: hidden }` stays set — forever.
 *    The page could not scroll again until reload. Dragging a window across
 *    990px is exactly what you do when checking mobile view, which is why this
 *    only showed up now.
 *
 * 2. THE BURGER WAS NOT A TOGGLE. It only ever called open(). Tapping it a
 *    second time re-opened an already-open drawer, re-locked the scroll and
 *    stole focus. Nothing about it said "closed", so it read as dead.
 *
 * 3. TWO FILES OWNED THE SCROLL LOCK AND THEY DISAGREED. This one set
 *    `body.style.overflow`; the video lightbox in solvane-reviews.js set
 *    `documentElement.style.overflow`. Close one while the other is open and
 *    the page is left half-locked. They now share one reference-counted lock.
 *
 * DO NOT set body overflow directly from anywhere. Use SvScrollLock, and keep
 * open() and close() idempotent — every fault above came from a state change
 * that assumed it could only happen once.
 *
 * ⚠ AND THE ONE THAT WAS STILL LEFT — same day, second report
 *
 * "Open it at the top of the page and it works. Scroll down first and it opens
 * somewhere I can't see, can't scroll, can't close."
 *
 * The drawer is `position: fixed; inset: 0`, which should mean the viewport.
 * It did not, because the drawer markup lives INSIDE <sv-header>, and
 * `.sv-header--sticky.is-hidden` sets `transform: translateY(-100%)`. **A
 * transformed ancestor becomes the containing block for its fixed
 * descendants.** So once the header had scrolled and hidden itself, the
 * drawer's `inset: 0` resolved against the header's own 106px box instead of
 * the viewport, and the -100% translate carried it off the top of the screen.
 * Measured: 390x800 at the top of the page, 390x106 at top:-107 after
 * scrolling. Scroll was already locked and the close button was off-screen
 * with it, so there was no way out.
 *
 * At scroll position zero the header carries no transform, which is exactly
 * why it worked at the top and nowhere else.
 *
 * THE FIX: the drawer is portaled to <body> on init, so no ancestor can ever
 * capture it. **Do not move the drawer back inside the header markup**, and be
 * aware that adding transform, filter, perspective, will-change or contain to
 * any ancestor of a fixed element does this same thing silently.
 * ---------------------------------------------------------------------------
 */

/* One reference-counted scroll lock, shared by every overlay on the site.
   Defined defensively so load order between files never matters. */
window.SvScrollLock = window.SvScrollLock || {
  depth: 0,
  prevHtml: '',
  prevBody: '',
  on: function () {
    if (this.depth++ > 0) return;
    this.prevHtml = document.documentElement.style.overflow;
    this.prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  },
  off: function (force) {
    if (this.depth === 0) return;
    this.depth = force ? 0 : this.depth - 1;
    if (this.depth > 0) return;
    document.documentElement.style.overflow = this.prevHtml;
    document.body.style.overflow = this.prevBody;
  },
};

class SvHeader extends HTMLElement {
  connectedCallback() {
    // On a re-connect the drawer is no longer a child — it was portaled out.
    this.drawer =
      this.querySelector('[data-sv-drawer]') ||
      this.portaledDrawer ||
      document.querySelector('body > [data-sv-drawer]') ||
      null;
    this.openBtn = this.querySelector('[data-sv-drawer-open]');
    this.isOpen = false;

    if (this.drawer && this.openBtn) {
      /* Portal the drawer to <body>. It is position:fixed and must resolve
         against the viewport, which it cannot do while any ancestor carries a
         transform — and this header transforms itself to hide on scroll. */
      if (this.drawer.parentElement !== document.body) {
        document.body.appendChild(this.drawer);
      }
      this.portaledDrawer = this.drawer;
      this.closeBtn = this.drawer.querySelector('[data-sv-drawer-close]');

      // A theme-editor re-render can drop a live element mid-open. Start closed.
      this.drawer.hidden = true;
      this.drawer.classList.remove('is-open');

      this.onToggle = this.toggle.bind(this);
      this.onClose = () => this.close();
      this.onKey = this.handleKey.bind(this);

      this.openBtn.addEventListener('click', this.onToggle);
      // Guarded: a reconnect must not stack a second set of listeners.
      if (this.drawer.dataset.svBound !== '1') {
        this.drawer.dataset.svBound = '1';
        if (this.closeBtn) this.closeBtn.addEventListener('click', this.onClose);
        this.drawer.addEventListener('click', (e) => {
          if (e.target === this.drawer) this.close();
        });
        this.drawer.querySelectorAll('a').forEach((a) =>
          a.addEventListener('click', this.onClose)
        );
      }

      /* The drawer is a mobile control. Above the breakpoint the burger is
         display:none, so if the viewport gets there while the drawer is open
         there is no longer any way to close it — and the scroll lock would
         outlive the thing that set it. */
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.onBreakpoint = (e) => {
        if (e.matches && this.isOpen) this.close({ returnFocus: false });
      };
      if (this.desktop.addEventListener) this.desktop.addEventListener('change', this.onBreakpoint);
      else if (this.desktop.addListener) this.desktop.addListener(this.onBreakpoint);
    }

    if (this.hasAttribute('data-sticky')) this.initSticky();
  }

  disconnectedCallback() {
    // Never leave a lock behind that nothing owns any more.
    if (this.isOpen) window.SvScrollLock.off();
    this.isOpen = false;
    document.removeEventListener('keydown', this.onKey);
    clearTimeout(this.hideTimer);
    if (this.desktop) {
      if (this.desktop.removeEventListener) this.desktop.removeEventListener('change', this.onBreakpoint);
      else if (this.desktop.removeListener) this.desktop.removeListener(this.onBreakpoint);
    }
    /* A theme-editor re-render discards this element for good, and its portaled
       drawer must go with it or every re-render leaves another one in <body>.
       Deferred, so a transient move in the DOM does not destroy a live drawer. */
    setTimeout(() => {
      if (this.isConnected) return;
      const d = this.portaledDrawer;
      if (!d || d.parentElement !== document.body) return;
      // A replacement header may have adopted it already — leave that one alone.
      const live = document.querySelector('sv-header');
      if (live && live !== this && live.portaledDrawer === d) return;
      d.remove();
    }, 0);
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    clearTimeout(this.hideTimer);
    // Never open with the header mid-hide behind the overlay.
    this.classList.remove('is-hidden');
    this.drawer.hidden = false;
    // Next frame, so the transition has a starting state to animate from.
    requestAnimationFrame(() => this.drawer.classList.add('is-open'));
    this.openBtn.setAttribute('aria-expanded', 'true');
    window.SvScrollLock.on();
    document.addEventListener('keydown', this.onKey);
    const first = this.drawer.querySelector('a, button');
    if (first) first.focus();
  }

  close(opts) {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.drawer.classList.remove('is-open');
    this.openBtn.setAttribute('aria-expanded', 'false');
    window.SvScrollLock.off();
    document.removeEventListener('keydown', this.onKey);

    // Not when the breakpoint closed it — the burger is display:none up there.
    if (!opts || opts.returnFocus !== false) this.openBtn.focus();

    // Wait for the transition before hiding, so it doesn't snap shut.
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      if (!this.isOpen) this.drawer.hidden = true;
    }, 260);
  }

  handleKey(e) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }
    if (e.key !== 'Tab') return;

    const items = this.drawer.querySelectorAll('a[href], button:not([disabled])');
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ⚠ REWRITTEN 26 Sep 2026 — "on a phone the header keeps coming down and up
     while I scroll down". Two faults, both measured in a 390px harness before
     the fix: at 2px of scroll per frame the header was on screen for 100 of 100
     frames of a downward scroll, and every page jumped 81px the moment it stuck.

     1. THE THRESHOLD WAS PER FRAME. It hid only when ONE frame moved more than
        4px. A thumb scroll, and every momentum scroll as it slows, moves 1–4px
        a frame — so each slow frame counted as "not scrolling down" and brought
        the header back, and the next fast one hid it again. Travel is now
        added up per direction: 6px of going down hides it, 14px of going up
        brings it back, and anything smaller changes nothing. The top of the
        range is clamped, so the rubber-band bounce at the foot of an iPhone
        page is not read as scrolling up.

     2. STICKING TOOK IT OUT OF THE PAGE. `.is-stuck` is `position: fixed`, so
        the moment it applied, everything below moved up by the header's height,
        and back down when it cleared. Its section keeps the header's height now
        while it is stuck, so nothing underneath moves. It also sticks already
        hidden, with no transition, so it never slides across the screen on its
        way out — it only ever animates coming back.

     Clears back into the page only once the header's own place is in view
     again, which is where the two positions coincide and there is no jump. */
  initSticky() {
    const HIDE_AFTER = 6;
    const SHOW_AFTER = 14;
    const slot = this.parentElement;
    let lastY = Math.max(0, window.scrollY);
    let travel = 0;
    let ticking = false;

    const instant = (fn) => {
      this.style.transition = 'none';
      fn();
      void this.offsetHeight; // commit the state before the transition returns
      this.style.transition = '';
    };

    const stick = (hidden) => {
      if (this.classList.contains('is-stuck')) return;
      instant(() => {
        // An overlay header is already out of the page; only an in-page one
        // leaves a hole.
        if (slot && getComputedStyle(this).position !== 'absolute') {
          slot.style.height = this.offsetHeight + 'px';
        }
        this.classList.add('is-stuck');
        this.classList.toggle('is-hidden', hidden);
      });
    };

    const unstick = () => {
      if (!this.classList.contains('is-stuck')) return;
      instant(() => {
        this.classList.remove('is-stuck', 'is-hidden');
        if (slot) slot.style.height = '';
      });
    };

    const update = () => {
      ticking = false;
      if (this.isOpen) return;

      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const y = Math.min(Math.max(0, window.scrollY), max);
      const dy = y - lastY;
      lastY = y;
      if (dy > 0) travel = travel > 0 ? travel + dy : dy;
      else if (dy < 0) travel = travel < 0 ? travel + dy : dy;

      // Where the header lives in the page. Its section keeps that height while
      // it is stuck, so this reads the same either way.
      const top = slot ? slot.getBoundingClientRect().top + window.scrollY : 0;
      const h = this.offsetHeight;
      const stuck = this.classList.contains('is-stuck');

      if (y <= top) {
        unstick();
        return;
      }

      if (!stuck) {
        // Let it scroll away with the page, and take it over only once it has
        // fully left the screen: hidden if going down, shown if coming back up.
        if (y > top + h) {
          if (travel > 0) stick(true);
          else if (travel <= -SHOW_AFTER) stick(false);
        }
        return;
      }

      // Stuck, and its own place is partly on screen: never leave that as an
      // empty band.
      if (y <= top + h) {
        this.classList.remove('is-hidden');
        return;
      }

      if (travel >= HIDE_AFTER) this.classList.add('is-hidden');
      else if (travel <= -SHOW_AFTER) this.classList.remove('is-hidden');
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );

    // The held height has to follow the header across a breakpoint.
    window.addEventListener('resize', () => {
      if (slot && this.classList.contains('is-stuck') && slot.style.height) {
        slot.style.height = this.offsetHeight + 'px';
      }
    });
  }
}

if (!customElements.get('sv-header')) customElements.define('sv-header', SvHeader);
